import { Flex, Icon, Tooltip } from '@chakra-ui/react';
import {
  SearchSm,
  ZoomIn,
  ZoomOut,
  Download01,
  Printer,
  Expand03,
  Minimize01,
} from '@kula-ai/icons-core';
import { type ReactNode } from 'react';

interface ToolButtonProps {
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  children: ReactNode;
}

const ToolButton = ({
  label,
  onClick,
  disabled,
  children,
}: ToolButtonProps) => (
  <Tooltip label={label} placement="bottom" hasArrow openDelay={150}>
    <Flex
      as="button"
      type="button"
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      align="center"
      justify="center"
      w="32px"
      h="32px"
      borderRadius="6px"
      transition="background-color 0.15s"
      _hover={disabled ? {} : { bg: 'secondary-50' }}
      _disabled={{ opacity: 0.4, cursor: 'not-allowed' }}
      cursor={disabled ? 'not-allowed' : 'pointer'}
    >
      {children}
    </Flex>
  </Tooltip>
);

export interface DocxViewerToolbarProps {
  onZoomIn: () => void;
  onZoomOut: () => void;
  canZoomIn: boolean;
  canZoomOut: boolean;
  onDownload: () => void;
  onPrint: () => void;
  isFullscreen?: boolean;
  onFullscreen: () => void;
  onSearchClick?: () => void;
  searchSlot?: ReactNode;
  isPrinting?: boolean;
}

export const DocxViewerToolbar = ({
  onZoomIn,
  onZoomOut,
  canZoomIn,
  canZoomOut,
  onDownload,
  onPrint,
  isFullscreen = false,
  onFullscreen,
  onSearchClick,
  searchSlot,
  isPrinting = false,
}: DocxViewerToolbarProps) => {
  return (
    <Flex
      align="center"
      justify="space-between"
      gap="12px"
      px="12px"
      py="8px"
      bg="white"
      borderBottom="1px solid"
      borderColor="secondary-200"
      borderTopRadius="sm"
      flexShrink={0}
    >
      <Flex align="center" gap="4px">
        {searchSlot ?? (
          <ToolButton label="Search" onClick={onSearchClick}>
            <Icon as={SearchSm} boxSize="20px" color="secondary-700" />
          </ToolButton>
        )}
      </Flex>

      <Flex align="center" gap="4px">
        <ToolButton label="Zoom out" onClick={onZoomOut} disabled={!canZoomOut}>
          <Icon as={ZoomOut} boxSize="18px" color="secondary-700" />
        </ToolButton>
        <ToolButton label="Zoom in" onClick={onZoomIn} disabled={!canZoomIn}>
          <Icon as={ZoomIn} boxSize="18px" color="secondary-700" />
        </ToolButton>
        <ToolButton label="Download" onClick={onDownload}>
          <Icon as={Download01} boxSize="20px" color="secondary-700" />
        </ToolButton>
        <ToolButton label="Print" onClick={onPrint} disabled={isPrinting}>
          <Icon as={Printer} boxSize="20px" color="secondary-700" />
        </ToolButton>
        <ToolButton
          label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          onClick={onFullscreen}
        >
          <Icon
            as={isFullscreen ? Minimize01 : Expand03}
            boxSize="20px"
            color="secondary-700"
          />
        </ToolButton>
      </Flex>
    </Flex>
  );
};
